import { useEffect, useMemo, useState } from 'react';
import { admin as adminApi, type AdminClient } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { AdminGuard } from '@/components/AdminGuard';
import { AdminSubNav } from '@/components/AdminSubNav';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/**
 * /app/admin/clients — admin-only roster of every organization on Poof.
 *
 * Wrapped in <AdminGuard> so non-admins see NOTHING that hints the
 * page exists — no title, no sub-nav, no "access required" message.
 * They get the standalone <NotFound /> page, indistinguishable from
 * any other unrouted URL. Defense in depth: /api/admin/clients itself
 * still checks requireAdmin server-side and returns 403 to non-admins.
 */
export default function AdminClients() {
  return (
    <AdminGuard>
      <AdminClientsContent />
    </AdminGuard>
  );
}

function AdminClientsContent() {
  const [clients, setClients] = useState<AdminClient[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    adminApi
      .clients()
      .then((r) => setClients(r.clients))
      .catch((e) => setErr((e as Error).message || 'Failed to load clients.'));
  }, []);

  const filtered = useMemo(() => {
    if (!clients) return null;
    if (!q.trim()) return clients;
    const needle = q.trim().toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        (c.ownerEmail || '').toLowerCase().includes(needle) ||
        c.organizationId.toLowerCase().includes(needle),
    );
  }, [clients, q]);

  const totals = useMemo(() => {
    if (!clients) return null;
    return {
      orgs: clients.length,
      members: clients.reduce((s, c) => s + c.memberCount, 0),
      links: clients.reduce((s, c) => s + c.linkCount, 0),
      clicks: clients.reduce((s, c) => s + c.clicks30d, 0),
    };
  }, [clients]);

  return (
    <DashboardLayout title="Admin · Clients">
      <AdminSubNav />

      {err && (
        <p className="mt-6 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {err}
        </p>
      )}

      {!err && (
        <>
          {/* Aggregate row across the whole platform — useful at a glance. */}
          <div className="mb-5 grid gap-3 sm:grid-cols-4">
            <SummaryStat label="Organizations" value={totals?.orgs} />
            <SummaryStat label="Total members" value={totals?.members} />
            <SummaryStat label="Total links" value={totals?.links} />
            <SummaryStat label="Clicks (30d)" value={totals?.clicks} />
          </div>

          <div className="mb-4 flex items-center gap-3">
            <h2 className="font-display text-lg font-bold tracking-tight">
              All organizations
            </h2>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / email / id…"
              className="ml-auto w-full max-w-xs rounded-full border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2 text-sm outline-none transition focus:border-[var(--color-ring)] sm:w-72"
            />
          </div>

          {!filtered && (
            <div className="space-y-2">
              <div className="h-14 animate-pulse rounded-xl bg-[var(--surface-glass)]" />
              <div className="h-14 animate-pulse rounded-xl bg-[var(--surface-glass)]" />
              <div className="h-14 animate-pulse rounded-xl bg-[var(--surface-glass)]" />
            </div>
          )}

          {filtered && filtered.length === 0 && (
            <p className="rounded-2xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-on-surface-variant)]">
              {q.trim()
                ? `No organizations match "${q}".`
                : 'No organizations registered yet.'}
            </p>
          )}

          {filtered && filtered.length > 0 && (
            <div className="overflow-x-auto rounded-2xl border border-[var(--color-border)]">
              <table className="w-full text-sm">
                <thead className="bg-[var(--surface-glass)] text-left text-xs uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
                  <tr>
                    <Th>Organization</Th>
                    <Th>Owner</Th>
                    <Th>Plan</Th>
                    <Th align="right">Members</Th>
                    <Th align="right">Links</Th>
                    <Th align="right">Clicks · 30d</Th>
                    <Th align="right">Last seen</Th>
                    <Th align="right">Joined</Th>
                    <Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr
                      key={c.organizationId}
                      className="border-t border-[var(--color-border)] transition hover:bg-[var(--surface-glass)]/60"
                    >
                      <Td>
                        <div className="font-medium">{c.name}</div>
                        <div className="font-mono text-[10px] text-[var(--color-on-surface-variant)]">
                          {c.organizationId}
                        </div>
                      </Td>
                      <Td>
                        <span className="text-[var(--color-on-surface-variant)]">
                          {c.ownerEmail || '—'}
                        </span>
                      </Td>
                      <Td>
                        <PlanBadge plan={c.plan} />
                      </Td>
                      <Td align="right" mono>
                        {c.memberCount.toLocaleString()}
                      </Td>
                      <Td align="right" mono>
                        {c.linkCount.toLocaleString()}
                      </Td>
                      <Td align="right" mono>
                        {c.clicks30d.toLocaleString()}
                      </Td>
                      <Td align="right">
                        <span
                          className="text-xs text-[var(--color-on-surface-variant)]"
                          title={
                            c.lastSeen ? new Date(c.lastSeen).toISOString() : 'No sessions ever'
                          }
                        >
                          {c.lastSeen ? formatRelative(c.lastSeen) : '—'}
                        </span>
                      </Td>
                      <Td align="right">
                        <span
                          className="text-xs text-[var(--color-on-surface-variant)]"
                          title={new Date(c.createdAt).toISOString()}
                        >
                          {formatRelative(c.createdAt)}
                        </span>
                      </Td>
                      <Td align="right">
                        {c.ownerUserId ? (
                          <ImpersonateButton
                            userId={c.ownerUserId}
                            email={c.ownerEmail || '(no email)'}
                          />
                        ) : (
                          <span className="text-xs text-[var(--color-on-surface-variant)]/40">
                            —
                          </span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}

function SummaryStat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--surface-glass)] p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">
        {value === undefined ? '—' : value.toLocaleString()}
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th className={`px-3 py-2.5 font-semibold ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  mono = false,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  mono?: boolean;
}) {
  return (
    <td
      className={`px-3 py-2.5 align-top ${align === 'right' ? 'text-right' : 'text-left'} ${
        mono ? 'font-mono tabular-nums' : ''
      }`}
    >
      {children}
    </td>
  );
}

function PlanBadge({ plan }: { plan: AdminClient['plan'] }) {
  const styles: Record<AdminClient['plan'], string> = {
    free: 'bg-[var(--color-on-surface-variant)]/10 text-[var(--color-on-surface-variant)]',
    pro: 'bg-[#7c4dff]/15 text-[#cdbdff]',
    agency: 'bg-[#16a34a]/15 text-[#7dffa2]',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] ${styles[plan]}`}
    >
      {plan}
    </span>
  );
}

/** Relative time, drops into absolute date after a week. Used for both
 *  "joined" and "last seen" columns so the two read consistently.
 *  Granularity:  "just now" / "12m ago" / "3h ago" / "5d ago" / "Mar 14"
 *  / "Mar 14 2024". */
function formatRelative(ms: number): string {
  const ageMs = Date.now() - ms;
  if (ageMs < 60_000) return 'just now';
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(ageMs / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(ageMs / 86_400_000);
  if (days < 7) return `${days}d ago`;
  const d = new Date(ms);
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

/**
 * "Impersonate" action — opens a confirm dialog explaining the
 * consequences, then calls /api/admin/impersonate which swaps the
 * admin's session cookie for one belonging to the target user. After
 * success, hard-reload to /app so every cached query refetches under
 * the new identity.
 */
function ImpersonateButton({ userId, email }: { userId: string; email: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onConfirm() {
    setBusy(true);
    setErr(null);
    try {
      await adminApi.impersonate(userId);
      // Hard reload so every cached query (session, links, analytics)
      // refetches under the target user's identity. Soft navigate
      // would keep stale admin state in component memory.
      window.location.assign('/app');
    } catch (e) {
      setErr((e as Error).message || 'Failed to impersonate.');
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full border border-[var(--color-border)] bg-transparent px-3 py-1 text-xs font-medium text-[var(--color-on-surface-variant)] transition hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-foreground)]"
      >
        Impersonate
      </button>
      <ConfirmDialog
        open={open}
        onClose={() => {
          if (!busy) {
            setOpen(false);
            setErr(null);
          }
        }}
        onConfirm={onConfirm}
        title={`Sign in as ${email}?`}
        description={
          <>
            <p>
              You&apos;ll be signed in as <strong>{email}</strong> and the dashboard
              will reflect their account exactly — their links, pages, analytics,
              and settings.
            </p>
            <p className="mt-2 text-[var(--color-muted-foreground)]">
              To return to your own account, sign out and sign back in. The
              impersonation session lasts 8 hours.
            </p>
            {err && (
              <p className="mt-2 text-[var(--color-destructive)]">{err}</p>
            )}
          </>
        }
        confirmLabel={busy ? 'Signing in…' : 'Impersonate'}
      />
    </>
  );
}
